<template>
  <form novalidate class="border-b border-gray-100 bg-white px-4 py-4 sm:px-6" @submit.prevent="save" @keydown.esc="$emit('close')">
    <fieldset :disabled="saving">
      <legend :id="idPrefix + '-legend'" class="text-sm font-medium text-gray-900">{{ $t('projects.drip_title') }}</legend>
      <p class="mt-1 text-sm text-gray-600">{{ $t('projects.drip_help') }}</p>

      <div class="mt-3 space-y-3">
        <label class="flex items-center gap-2 text-sm text-gray-700">
          <input type="radio" :name="idPrefix + '-mode'" :value="modes.immediate" v-model="mode"
                 class="h-4 w-4 border-gray-300 text-bpblue focus:ring-bpblue">
          {{ $t('projects.drip_immediate') }}
        </label>

        <div>
          <label class="flex items-center gap-2 text-sm text-gray-700">
            <input type="radio" :name="idPrefix + '-mode'" :value="modes.days" v-model="mode"
                   class="h-4 w-4 border-gray-300 text-bpblue focus:ring-bpblue">
            {{ $t('projects.drip_days_option') }}
          </label>
          <div v-if="mode === modes.days" class="mt-2 ml-6 flex flex-wrap items-center gap-2">
            <label :for="idPrefix + '-days'" class="sr-only">{{ $t('projects.drip_days_label') }}</label>
            <input :id="idPrefix + '-days'" ref="days" v-model.number="days" type="number" inputmode="numeric"
                   :min="minDays" :max="maxDays" step="1"
                   :aria-invalid="errors.days ? 'true' : 'false'" :aria-describedby="errors.days ? idPrefix + '-days-error' : null"
                   class="block w-24 rounded-md border-gray-300 shadow-sm focus:border-bpblue focus:ring-bpblue sm:text-sm">
            <span class="text-sm text-gray-700">{{ $t('projects.drip_days_suffix') }}</span>
            <p v-if="errors.days" :id="idPrefix + '-days-error'" class="w-full text-sm text-red-600">{{ errors.days }}</p>
          </div>
        </div>

        <div>
          <label class="flex items-center gap-2 text-sm text-gray-700">
            <input type="radio" :name="idPrefix + '-mode'" :value="modes.date" v-model="mode"
                   class="h-4 w-4 border-gray-300 text-bpblue focus:ring-bpblue">
            {{ $t('projects.drip_date_option') }}
          </label>
          <div v-if="mode === modes.date" class="mt-2 ml-6">
            <label :for="idPrefix + '-date'" class="sr-only">{{ $t('projects.drip_date_label') }}</label>
            <input :id="idPrefix + '-date'" ref="date" v-model="date" type="date"
                   :aria-invalid="errors.date ? 'true' : 'false'" :aria-describedby="errors.date ? idPrefix + '-date-error' : null"
                   class="block rounded-md border-gray-300 shadow-sm focus:border-bpblue focus:ring-bpblue sm:text-sm">
            <p v-if="errors.date" :id="idPrefix + '-date-error'" class="mt-2 text-sm text-red-600">{{ errors.date }}</p>
          </div>
        </div>
      </div>

      <p v-if="errors.general" class="mt-3 text-sm text-red-600" role="alert">{{ errors.general }}</p>

      <div class="mt-4 flex justify-end gap-2">
        <button type="button" v-on:click="$emit('close')"
                class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
          {{ $t('general.cancel') }}
        </button>
        <button type="submit" :disabled="!isDirty"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-blue-500 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-bpblue">
          <font-awesome-icon v-if="saving" class="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" icon="spinner"/>
          {{ $t('general.save') }}
        </button>
      </div>
    </fieldset>
  </form>
</template>

<script>
import {useProjectsStore} from "@/stores/projects";
import NotificationService from "@/services/notification/NotificationService";
import {DRIP_MAX_DAYS, DRIP_MIN_DAYS, DRIP_MODES, scheduleOf} from "@/views/projects/ProjectEdit/Curriculum/dripSchedule";

/*
 * Programación (drip) de una sección, dentro de la propia sección (FE-062, D5):
 * disponible desde la matrícula, N días después o en una fecha fija. Guarda con
 * `PUT …/sections/{sección}` (LMS-108/109) y pinta junto al campo los 422 de
 * `ValidatesDripSchedule`.
 */
export default {
  name: "SectionScheduleComponent",
  props: {
    section: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'saved'],
  setup() {
    return {
      projectsStore: useProjectsStore()
    }
  },
  data() {
    const schedule = scheduleOf(this.section)
    return {
      modes: DRIP_MODES,
      minDays: DRIP_MIN_DAYS,
      maxDays: DRIP_MAX_DAYS,
      initial: schedule,
      mode: schedule.mode,
      days: schedule.days,
      date: schedule.date,
      saving: false,
      errors: {},
    }
  },
  computed: {
    idPrefix() {
      return 'section-schedule-' + this.section.uuid
    },
    isDirty() {
      if (this.mode !== this.initial.mode) return true
      if (this.mode === DRIP_MODES.days) return this.days !== this.initial.days
      if (this.mode === DRIP_MODES.date) return this.date !== this.initial.date
      return false
    }
  },
  watch: {
    mode(mode) {
      this.errors = {}
      this.$nextTick(() => this.$refs[mode]?.focus())
    }
  },
  mounted() {
    // Foco en la opción elegida, como haría un grupo de radios al entrar con Tab.
    this.$el.querySelector('input[type="radio"]:checked')?.focus()
  },
  methods: {
    validate() {
      this.errors = {}
      if (this.mode === DRIP_MODES.days
          && !(Number.isInteger(this.days) && this.days >= DRIP_MIN_DAYS && this.days <= DRIP_MAX_DAYS)) {
        this.errors = {days: this.$t('projects.drip_days_invalid', {min: DRIP_MIN_DAYS, max: DRIP_MAX_DAYS})}
      }
      if (this.mode === DRIP_MODES.date && !/^\d{4}-\d{2}-\d{2}$/.test(this.date ?? '')) {
        this.errors = {date: this.$t('projects.drip_date_invalid')}
      }
      return !Object.keys(this.errors).length
    },
    payload() {
      return {
        drip_days_after_enrolment: this.mode === DRIP_MODES.days ? this.days : null,
        available_from: this.mode === DRIP_MODES.date ? this.date : null,
      }
    },
    save() {
      if (!this.isDirty || !this.validate()) return

      this.saving = true
      this.projectsStore.updateSectionSchedule(this.section, this.payload())
          .then(() => {
            NotificationService.showNotification(this.$t('projects.drip_saved'), "success")
            this.$emit('saved')
          })
          .catch((error) => {
            if (!this.applyServerErrors(error)) {
              NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
            }
          })
          .finally(() => {
            this.saving = false
          })
    },
    // 422 de `ValidatesDripSchedule`: se traducen aquí y se pintan junto a su campo.
    applyServerErrors(error) {
      const errors = error?.response?.status === 422 ? error.response.data?.errors : null
      if (!errors) return false

      if (errors.drip_days_after_enrolment) {
        this.errors = {days: this.$t('projects.drip_days_invalid', {min: DRIP_MIN_DAYS, max: DRIP_MAX_DAYS})}
      } else if (errors.available_from) {
        this.errors = {date: this.$t('projects.drip_date_invalid')}
      } else {
        this.errors = {general: Object.values(errors).flat().join(' ')}
      }
      return true
    }
  }
}
</script>
